import { getArticles, getArticlesByArtNr } from '@/data/api/products/requests';
import { Article } from '@/types/Article';

class MemKeyval {
  private cache: Record<string, any>;

  constructor() {
    this.cache = {};
  }

  get(key: string) {
    return this.cache[key];
  }

  set(key: string, value: any) {
    this.cache[key] = value;
  }

  delete(key: string) {
    delete this.cache[key];
  }

  clear() {
    this.cache = {};
  }
}

export default class ArticleHelper {
  private static cacheDuration = 15 * 60 * 1000; // 15 minutes
  private static cache = new MemKeyval();
  private static expiration = new MemKeyval();

  // Private constructor to prevent instantiation
  private constructor() {}

  public static getFromCache(artnrOrId: string): Article | undefined {
    return this.cache.get(artnrOrId);
  }

  public static async get(
    { id, artnr }: { id?: string; artnr?: string } = {},
    unfiltered?: boolean
  ): Promise<Article | undefined> {
    if (!id && !artnr) {
      console.error('No id or artnr provided');
      return;
    }
    if (id && artnr) {
      console.error('Both id and artnr provided');
      return;
    }

    let article: Article | undefined;
    if (id) {
      await this.getById(id).then((articles) => {
        article = articles[id];
      });
    } else if (artnr) {
      await this.getByArtNr(artnr, unfiltered).then((articles) => {
        article = articles[artnr];
      });
    }

    return article;
  }

  public static async getAll(
    { ids, artnrs }: { ids?: string[]; artnrs?: string[] } = {},
    unfiltered?: boolean
  ): Promise<Record<string, Article>> {
    if (!ids && !artnrs) {
      console.error('No ids or artnrs provided');
      return {};
    }

    let articles: Record<string, Article> = {};
    if (ids) {
      await this.getById(ids).then((fetchedArticles) => {
        articles = { ...articles, ...fetchedArticles };
      });
    }
    if (artnrs) {
      await this.getByArtNr(artnrs, unfiltered).then((fetchedArticles) => {
        articles = { ...articles, ...fetchedArticles };
      });
    }

    return articles;
  }

  /* Private methods */
  private static getCached(artnrOrId: string): Article | undefined {
    const cached = this.cache.get(artnrOrId);
    const expiration = this.expiration.get(artnrOrId);

    if (!cached || !expiration || expiration < Date.now()) {
      return undefined;
    }

    return cached;
  }

  private static setCached(article: Article): void {
    this.cache.set(article.id, article);
    this.cache.set(article.ArtNr, article);
    this.expiration.set(article.id, Date.now() + this.cacheDuration);
    this.expiration.set(article.ArtNr, Date.now() + this.cacheDuration);
  }

  private static getExistingAndMissing(cacheKeys: string[]): { existing: Record<string, Article>; missing: string[] } {
    const existing: Record<string, Article> = {};
    const missing: string[] = [];
    for (const key of cacheKeys) {
      const cached = this.getCached(key);
      if (cached !== undefined) {
        existing[key] = cached!;
      } else {
        missing.push(key);
      }
    }
    return {
      existing,
      missing,
    };
  }

  private static async getByArtNr(artnrs: string | string[], unfiltered?: boolean): Promise<Record<string, Article>> {
    const existingAndMissing = this.getExistingAndMissing(Array.isArray(artnrs) ? artnrs : [artnrs]);
    if (!existingAndMissing.missing.length) {
      return existingAndMissing.existing;
    }

    await getArticlesByArtNr(existingAndMissing.missing, unfiltered).then((articles: Record<string, Article>) => {
      for (const key in articles) {
        const article = articles[key];
        this.setCached(article);
        existingAndMissing.existing[article.ArtNr] = article;
      }
    });
    return existingAndMissing.existing;
  }

  private static async getById(ids: string | string[]): Promise<Record<string, Article>> {
    const existingAndMissing = this.getExistingAndMissing(Array.isArray(ids) ? ids : [ids]);
    if (!existingAndMissing.missing.length) {
      return existingAndMissing.existing;
    }

    await getArticles(existingAndMissing.missing).then((articles: Record<string, Article>) => {
      for (const key in articles) {
        const article = articles[key];
        this.setCached(article);
        existingAndMissing.existing[article.id] = article;
      }
    });
    return existingAndMissing.existing;
  }

  public static debug() {
    console.debug('Article cache', this.cache);
  }

  public static clearCache() {
    this.cache.clear();
    this.expiration.clear();
  }
}

export function articleIsFreightTonArticle(article: Article): boolean {
  return (
    article?.alternativeUnits.some((unit) => unit.alternateType === 2 && unit.alternate === 't') &&
    article.userDefinedField1Item?.trim() === 'FRAKT' &&
    // ['TL', 'TO'].includes(article?.discountGroupItem) &&
    article?.basicUnitOfMeasure === 'kg'
  );
}
